<script setup lang="ts">
import type { ContentPackage } from '~/models/Content/ContentPackage'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons'
import { KsIcon, KsSkeleton, KsSkeletonWrapper } from '@aschehoug/kloss'
import { getFlytProgressLocally } from '~/utils/flyt'
import useFilterStore from '~/stores/filter'
import { PendoFeature } from '~/models/Pendo'
import { useStaticProduct } from '~/composables/useStaticProduct'
import { useContentHelper } from '~/composables/useContentHelper'
import useContentApi from '~/api/contentApi'
import ShareContentDialog from '~/components/share/ShareContentDialog.vue'
import SimpleCard from '~/components/cards/SimpleCard.vue'
import GridCard from '~/components/cards/GridCard.vue'

const {
  header,
  index,
  gradeFilter = true,
} = defineProps<{
  header: ContentPackage
  index: number
  gradeFilter?: boolean
}>()

const { gradeFilterFunc } = useFilterStore()
const { isContentHeader } = useContentHelper()
const { findContents } = useContentApi()
const { Terminprover } = useStaticProduct()

const children = computed(() => {
  if (!data.value)
    return []
  return gradeFilter ? data.value.filter(gradeFilterFunc) : data.value
})

const isGrid = computed(() => isContentHeader(header) && header.viewMode === 'grid')
const noImage = computed(() => isContentHeader(header) && header.viewMode === 'task')

const { data, isLoading } = useQuery({
  enabled: computed(() => !!header.mainLocationId),
  queryKey: ['header-children', header.mainLocationId],
  queryFn: () => findContents({
    parentLocationIdCriterion: [header.mainLocationId],
    sortField: header.sortField,
    sortOrder: header.sortOrder,
  }),
  staleTime: Infinity,
})

const tracking = computed(() => header.pathString.startsWith(Terminprover.aunivers.pathString!)
  ? { 'data-pendo': PendoFeature.TermExam.NavigateTask }
  : {}
)
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="flex flex-row">
      <h2
        class="mr-4 font-tiempos text-4xl"
        v-text="header.title"
      />
      <ShareContentDialog
        :resource="header"
        shape="square"
        variant="primary"
        size="medium"
        :show-label="false"
        :stretch="false"
      />
    </div>
    <KsSkeletonWrapper
      v-if="isLoading"
      class="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
    >
      <KsSkeleton
        v-for="n in 6"
        :key="n"
        height="auto"
        class="aspect-video"
        border-radius="0px"
        background="var(--theme-5)"
      />
    </KsSkeletonWrapper>
    <ul
      v-else
      class="grid auto-rows-fr sm:grid-cols-2 md:grid-cols-3"
      :class="isGrid ? 'gap-6' : 'gap-[1px] lg:grid-cols-4'"
    >
      <li
        v-for="childContent in children"
        :key="childContent.locationId"
        class="grid"
        v-bind="tracking"
        :class="isGrid && 'h-fit'"
      >
        <GridCard
          v-if="isGrid"
          :resource="childContent"
          :loading="index === 0 ? 'eager' : 'lazy'"
        />
        <SimpleCard
          v-else
          :resource="childContent"
          :no-image
          :style="getFlytProgressLocally(childContent.locationId)?.completed
            ? {
              '--card-ring': 'var(--theme-5)',
              '--card-bg': 'var(--theme-20)',
              '--card-hover': 'var(--theme-20)',
            }
            : {}
          "
        >
          <template #status>
            <KsIcon
              v-if="getFlytProgressLocally(childContent.locationId)?.completed"
              :icon="faCheckCircle"
            />
          </template>
        </SimpleCard>
      </li>
    </ul>
  </section>
</template>
